import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import "@/App.css";
import SEO from "../components/shared/SEO";

// Navigation Component
const Navigation = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/5">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
      <div className="flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="flex gap-1">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-1.5 rounded-full bg-[#D4AF37]"
                style={{ height: `${12 + i * 6}px` }}
              />
            ))}
          </div>
          <div>
            <span className="text-white font-bold tracking-tight">LRS</span>
            <span className="hidden sm:block text-[10px] text-zinc-500 uppercase tracking-widest">Leitner Revenue Systems</span>
          </div>
        </Link>
        
        <Link
          to="/"
          className="flex items-center gap-2 text-zinc-400 hover:text-[#D4AF37] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </div>
    </div>
  </nav>
);

// Footer Component
const Footer = () => (
  <footer className="bg-[#0A0A0A] border-t border-white/5 py-12">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-1.5 rounded-full bg-[#D4AF37]"
                style={{ height: `${8 + i * 4}px` }}
              />
            ))}
          </div>
          <span className="text-white font-bold">LRS</span>
        </div>
        <p className="text-sm text-zinc-500">
          © {new Date().getFullYear()} Leitner Revenue Systems. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Results Page
function ResultsPage() {
  const metrics = [
    { metric: "3x", label: "Revenue influence increase", context: "At high-growth B2B company" },
    { metric: "5x", label: "SDR output improvement", context: "Within first 6 months" },
    { metric: "60%+", label: "Pipeline from SDR teams", context: "Vs. industry average of 30%" },
    { metric: "< 6 mo", label: "Payback on SDR investment", context: "Gross profit basis" }
  ];

  const testimonials = [
    {
      quote: "The system transformed how we think about pipeline. It's not about more activity—it's about better execution.",
      author: "VP of Sales",
      company: "Series B SaaS"
    },
    {
      quote: "Our SDR team went from a cost center to our primary growth engine.",
      author: "CRO",
      company: "B2B Tech Company"
    },
    {
      quote: "Finally, someone who understands that AI is a tool, not a strategy. The framework changed everything.",
      author: "CEO",
      company: "Growth-Stage Startup"
    }
  ];

  return (
    <div className="bg-[#0A0A0A] min-h-screen">
      <SEO 
        title="Results | Proven Revenue Impact"
        description="Real outcomes from real systems. Pipeline that converts, SDR teams that perform, and measurable revenue impact for Series B & C startups."
        path="/results"
        image="home"
      />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
            Results That Matter
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            Real outcomes from real systems. Not vanity metrics—pipeline that converts.
          </p>
        </div>
      </section>
      
      {/* Metrics Grid */}
      <section className="py-16 bg-[#1C1C1C]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            {metrics.map((item) => (
              <div key={item.label} className="p-8 bg-[#0A0A0A] rounded-xl border border-white/5">
                <div className="text-4xl lg:text-5xl font-bold text-[#D4AF37] mb-2">{item.metric}</div>
                <div className="text-lg text-white font-medium mb-1">{item.label}</div>
                <div className="text-sm text-zinc-500">{item.context}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Testimonials */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="text-2xl lg:text-3xl font-bold text-white text-center mb-12">
            What Leaders Say
          </h2>
          
          <div className="space-y-8">
            {testimonials.map((item) => (
              <div key={item.author} className="p-8 bg-[#1C1C1C] rounded-xl border border-white/5">
                <Quote className="w-8 h-8 text-[#D4AF37]/30 mb-4" />
                <p className="text-xl text-white leading-relaxed mb-6">
                  "{item.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <span className="text-[#D4AF37] font-bold text-sm">{item.author[0]}</span>
                  </div>
                  <div>
                    <div className="text-white font-medium">{item.author}</div>
                    <div className="text-sm text-zinc-500">{item.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16 bg-[#1C1C1C]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
            Ready to Build Your System?
          </h2>
          <p className="text-zinc-400 mb-8">
            Let's talk about what's broken and how to fix it.
          </p>
          <a
            href="https://calendly.com/leitner-revenue-systems/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-[#0A0A0A] rounded-lg font-semibold hover:bg-[#E8C547] transition-all"
          >
            Get Started
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default ResultsPage;
